import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { fetchMyOrganizationsRequest, fetchSubOrgsRequest } from '../services/organizationService.js'
import { toOrganization, toSubOrganization } from '../domain/model.js'
import { filterActiveOrgs, filterActiveSubOrgs } from '../utils/lifecycleUtils.js'

export const useOrgStore = defineStore('org', () => {
  const ACTIVE_ORG_STORAGE_KEY = 'active_org_id'
  const ACTIVE_SUB_ORG_STORAGE_KEY = 'active_sub_org_id'
  const organizations = ref([])
  const subOrgs = ref([])
  const loading = ref(false)
  const error = ref('')
  const trace = ref([])
  const log = (step, meta = {}) => {
    const msg = `${Date.now()} | ${step}`
    trace.value.push(msg)
    if (import.meta.env.DEV) {
      console.log('[ORG TRACE]', { msg, ...meta })
    }
  }
  const getStoredActiveOrgId = () => {
    const storedValue = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY)
    if (storedValue === null || storedValue === '') return null

    const numericValue = Number(storedValue)
    return Number.isNaN(numericValue) ? storedValue : numericValue
  }

  const clearActiveOrg = () => {
    activeOrgId.value = null
    activeOrgRole.value = null
    activeSubOrgId.value = null
    localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY)
    localStorage.removeItem(ACTIVE_SUB_ORG_STORAGE_KEY)
  }

  const getStoredActiveSubOrgId = () => {
    const storedValue = localStorage.getItem(ACTIVE_SUB_ORG_STORAGE_KEY)
    if (storedValue === null || storedValue === '') return null

    const numericValue = Number(storedValue)
    return Number.isNaN(numericValue) ? storedValue : numericValue
  }

  const activeOrgId = ref(getStoredActiveOrgId())
  const activeSubOrgId = ref(getStoredActiveSubOrgId())
  const subOrgsHydratedForOrgId = ref(null)
  const activeOrgRole = ref(null)
  const isOrgReady = computed(() => !!activeOrgId.value)
  const subOrgContextReady = computed(() => {
    if (!activeOrgId.value) return false
    return Number(subOrgsHydratedForOrgId.value) === Number(activeOrgId.value)
  })
  const contextReady = computed(() => isOrgReady.value && subOrgContextReady.value)
  const availableOrganizations = computed(() => filterActiveOrgs(organizations.value))
  const filteredSubOrgs = computed(() => {
    if (!activeOrgId.value) return []
    return filterActiveSubOrgs(subOrgs.value).filter((item) => Number(item?.organization_id) === Number(activeOrgId.value))
  })
  const activeOrganization = computed(() => {
    return organizations.value.find((org) => Number(org?.id) === Number(activeOrgId.value)) ?? null
  })

  const setActiveSubOrg = (subOrgId) => {
    if (subOrgId === null || subOrgId === undefined || subOrgId === '') {
      activeSubOrgId.value = null
      localStorage.removeItem(ACTIVE_SUB_ORG_STORAGE_KEY)
      return
    }

    const normalizedId = Number(subOrgId)
    const matchedSubOrg = filteredSubOrgs.value.find((item) => Number(item?.id) === normalizedId)

    if (!matchedSubOrg) {
      activeSubOrgId.value = null
      localStorage.removeItem(ACTIVE_SUB_ORG_STORAGE_KEY)
      return
    }

    activeSubOrgId.value = matchedSubOrg.id
    localStorage.setItem(ACTIVE_SUB_ORG_STORAGE_KEY, String(matchedSubOrg.id))
  }

  const setActiveOrg = (orgId) => {
    log('ACTIVE_ORG_SET_START', { orgId })
    const previousOrgId = activeOrgId.value
    const matchedOrg = availableOrganizations.value.find((org) => Number(org?.id) === Number(orgId))

    if (!matchedOrg) {
      clearActiveOrg()
      subOrgsHydratedForOrgId.value = null
      log('ACTIVE_ORG_SET_CLEARED', { orgId })
      return
    }

    if (Number(previousOrgId) !== Number(matchedOrg.id)) {
      setActiveSubOrg(null)
      subOrgsHydratedForOrgId.value = null
    }

    activeOrgId.value = matchedOrg.id
    activeOrgRole.value = toOrganization(matchedOrg).role
    localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, String(matchedOrg.id))
    if (import.meta.env.DEV) {
      console.log('[RBAC DEBUG]', {
        step: 'setActiveOrg',
        apiRole: matchedOrg.role,
        mappedRole: activeOrgRole.value,
        activeOrgRole: activeOrgRole.value,
      })
    }
    log('ACTIVE_ORG_SET_DONE', { activeOrgId: activeOrgId.value, activeOrgRole: activeOrgRole.value })
  }

  const setOrganizations = (orgs) => {
    organizations.value = Array.isArray(orgs)
      ? orgs.map((org) => {
          const normalized = toOrganization(org)
          const mappedRole = normalized.role
          if (import.meta.env.DEV) {
            console.log('[RBAC DEBUG]', {
              step: 'setOrganizations',
              orgId: org?.id,
              apiRole: org?.role,
              mappedRole,
            })
          }
          return {
            ...org,
            ...normalized,
          }
        })
      : []
    log('ROLES_RECEIVED', { organizations: organizations.value.length })

    if (activeOrgId.value === null) {
      activeOrgRole.value = null
      log('ROLES_RECEIVED_NO_ACTIVE_ORG')
      return
    }

    const matchedOrg = organizations.value.find((org) => Number(org?.id) === Number(activeOrgId.value))

    if (!matchedOrg) {
      clearActiveOrg()
      log('ROLES_RECEIVED_ACTIVE_ORG_MISSING')
      return
    }

    activeOrgId.value = matchedOrg.id
    activeOrgRole.value = toOrganization(matchedOrg).role
    if (import.meta.env.DEV) {
      console.log('[RBAC DEBUG]', {
        step: 'setOrganizations.active',
        apiRole: matchedOrg.role,
        mappedRole: activeOrgRole.value,
        activeOrgRole: activeOrgRole.value,
      })
    }
    log('ROLES_APPLIED_TO_ACTIVE_ORG', { activeOrgId: activeOrgId.value, activeOrgRole: activeOrgRole.value })
  }

  const fetchMyOrganizations = async (options = {}) => {
    log('RBAC_LOAD_START')
    loading.value = true
    error.value = ''

    try {
      const data = await fetchMyOrganizationsRequest()
      console.log('[ORG RAW RESPONSE]', data)
      setOrganizations(data)

      // Preserve existing selection, but default to first org when none is active.
      if (organizations.value.length > 0 && !activeOrganization.value) {
        setActiveOrg(organizations.value[0].id)
      }

      await fetchSubOrgs({ preferredSubOrgId: options.preferredSubOrgId })
      log('RBAC_LOAD_ORGS_DONE', { organizations: organizations.value.length })
      return { success: true, data: organizations.value }
    } catch (err) {
      console.error('[RBAC] Failed to load organizations', err)
      error.value = err?.response?.data?.detail ?? 'Failed to fetch organizations'
      log('RBAC_LOAD_ERROR', { error: error.value })
      return { success: false, error: error.value }
    } finally {
      loading.value = false
      log('RBAC_LOAD_END')
    }
  }

  const fetchSubOrgs = async (options = {}) => {
    if (!activeOrgId.value) {
      subOrgs.value = []
      subOrgsHydratedForOrgId.value = null
      setActiveSubOrg(null)
      return { success: true, data: [] }
    }

    loading.value = true
    error.value = ''

    try {
      const data = await fetchSubOrgsRequest(options?.status ?? 'active')
      subOrgs.value = Array.isArray(data)
        ? data.map((item) => ({
            ...item,
            ...toSubOrganization(item),
          }))
        : []

      const preferredSubOrgId = Number(options?.preferredSubOrgId)
      const hasPreferredSubOrgId = Number.isInteger(preferredSubOrgId) && preferredSubOrgId > 0
      const isCurrentValid = filteredSubOrgs.value.some((item) => Number(item?.id) === Number(activeSubOrgId.value))
      if (!isCurrentValid) {
        const preferredScopedSubOrg = hasPreferredSubOrgId
          ? filteredSubOrgs.value.find((item) => Number(item?.id) === preferredSubOrgId)
          : null
        const fallbackScopedSubOrg = filteredSubOrgs.value[0] ?? null
        const nextSubOrgId = preferredScopedSubOrg?.id ?? fallbackScopedSubOrg?.id ?? null
        setActiveSubOrg(nextSubOrgId)
      }

      // Mark hydration complete only after active sub-org selection is resolved.
      subOrgsHydratedForOrgId.value = Number(activeOrgId.value)

      return { success: true, data: subOrgs.value }
    } catch (err) {
      subOrgs.value = []
      subOrgsHydratedForOrgId.value = null
      setActiveSubOrg(null)
      error.value = err?.response?.data?.detail ?? 'Failed to fetch sub-organizations'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const initializeOrganizations = async (options = {}) => {
    log('RBAC_INIT_START')

    const ensureActiveOrgSubOrgs = async () => {
      if (!activeOrgId.value) {
        return { success: true, data: [] }
      }

      if (Number(subOrgsHydratedForOrgId.value) === Number(activeOrgId.value)) {
        return { success: true, data: subOrgs.value }
      }

      return fetchSubOrgs({ preferredSubOrgId: options.preferredSubOrgId })
    }

    // Session already has a resolved org context; avoid redundant bootstrap fetch.
    if (organizations.value.length > 0 && activeOrganization.value && activeOrgRole.value) {
      log('RBAC_INIT_SKIP_ALREADY_READY', { activeOrgId: activeOrgId.value, activeOrgRole: activeOrgRole.value })

      const subOrgResult = await ensureActiveOrgSubOrgs()
      if (!subOrgResult.success) {
        return subOrgResult
      }

      return { success: true, data: organizations.value }
    }

    const result = await fetchMyOrganizations({ preferredSubOrgId: options.preferredSubOrgId })

    if (!result.success) {
      log('RBAC_INIT_ERROR', { error: result.error })
      return result
    }

    if (!organizations.value.length) {
      clearActiveOrg()
      error.value = 'No organizations available for this user'
      log('RBAC_INIT_NO_ORGS')
      return { success: false, error: error.value }
    }

    if (organizations.value.length === 1) {
      setActiveOrg(organizations.value[0].id)
      const subOrgResult = await ensureActiveOrgSubOrgs()
      if (!subOrgResult.success) {
        return subOrgResult
      }
      log('RBAC_INIT_SINGLE_ORG_SELECTED', { activeOrgId: activeOrgId.value, activeOrgRole: activeOrgRole.value })
      return { success: true, data: organizations.value }
    }

    if (!activeOrganization.value) {
      setActiveOrg(organizations.value[0].id)
    }

    const subOrgResult = await ensureActiveOrgSubOrgs()
    if (!subOrgResult.success) {
      return subOrgResult
    }

    log('RBAC_READY', { activeOrgId: activeOrgId.value, activeOrgRole: activeOrgRole.value })

    return { success: true, data: organizations.value }
  }

  watch(
    () => activeOrgId.value,
    (nextOrgId, previousOrgId) => {
      if (nextOrgId === previousOrgId) return
      setActiveSubOrg(null)
    },
  )

  return {
    organizations,
    availableOrganizations,
    subOrgs,
    activeOrgId,
    activeSubOrgId,
    activeOrgRole,
    isOrgReady,
    subOrgContextReady,
    contextReady,
    filteredSubOrgs,
    activeOrganization,
    loading,
    error,
    trace,
    log,
    setOrganizations,
    setActiveOrg,
    setActiveSubOrg,
    fetchMyOrganizations,
    fetchSubOrgs,
    initializeOrganizations,
  }
})
